import {useEffect, useState} from 'react';
import {useParams} from "react-router";
import {
    getPurchaseOrderLineItem,
    completePurchaseOrderLine, addInventoryToCart
} from "../../api/Api";
import {Card, Row, Col, Table, Button, InputNumber, message} from 'antd';

import {TrashWidget} from "../widget/TrashWidget";
import {ProductDisplayWidget} from "./ProductDisplayWidget";
import {UnitType, PurchaseOrderItemStatusType,  CategoryType} from "../../api/Constants";
import {toUtcDateString} from "../../util/timeutils";
import {PurchaseOrderItemStatusWidget} from "../widget/PurchaseOrderItemStatusWidget";
import {CompletedPOLineItemView} from "./CompletedPOLineItemView";
import {displayToFixedUnit, fixedToDisplayUnit} from "../../util/unitutils";
import {InventorySelectWidget} from "./InventorySelectWidget";
import {QuantityEntryWidget} from "./QuantityEntryWidget";

export const POLineItemView = ({onApiFail, refresh, onRefresh, props}) => {
    const params = useParams();
    const purchaseId = params.purchaseId;
    const lineId = params.lineId;

    const [item, setItem] = useState(undefined);
    const [showInventory, setShowInventory] = useState(false);
    const [isBulk, setIsBulk] = useState(true);

    const [ingredients, setIngredients] = useState([]);
    const [product, setProduct] = useState(undefined);
    const [quantity, setQuantity] = useState(0);
    const [hours, setHours] = useState(undefined);
    const [bookQuantity, setBookQuantity] = useState(0);
    const [messageApi, contextHolder] = message.useMessage();
    const unit = UnitType.Each;

    const getProductId = () => {
        // product is ui selectable and can be used to override the productId in the line item
        if (product) {
            return product.id;
        }
        return item.productId;
    }

    const hasProductId = () => {
        const productId = getProductId();
        return !((productId === null) || (productId === undefined));
    }

    useEffect(() => {
        if (lineId === undefined) {
            return;
        }
        getPurchaseOrderLineItem(purchaseId, lineId).then(result => {
            console.log(`item ${JSON.stringify(result)}`);
            setItem(result);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

    }, [onApiFail, refresh, purchaseId, lineId]);

    const onOpenBulkInventory = () => {
        setShowInventory(true);
        setIsBulk(true);
    }

    const onOpenSupplyInventory = () => {
        setShowInventory(true);
        setIsBulk(false);
    }

    const onCloseInventory = () => {
        setShowInventory(false);
    }

    const onIngredientDelete = (index) => () => {
        const nextIngredients = [];
        for (let i=0; i<ingredients.length; i++) {
            if (i !== index) {
                nextIngredients.push(ingredients[i]);
            }
        }
        setIngredients(nextIngredients);
    }

    const selectIngredient = (ingredient) => {
        console.log(`ingredient ${ingredient.id} selected`);
        for (let i=0; i<ingredients.length; i++) {
            if (ingredients[i].id === ingredient.id) {
                return;
            }
        }
        const nextIngredients = [...ingredients];
        nextIngredients.push({
            ...ingredient,
            available: ingredient.quantity,
            quantity: 0,
            waste: 0
        });
        setIngredients(nextIngredients);
        setShowInventory(false);
    }

    const onIngredientQuantityChange = (index) => (value) => {
        const nextIngredients = [...ingredients];
        nextIngredients[index].quantity = value;
        setIngredients(nextIngredients);
    }

    const onIngredientWasteChange = (index) => (value) => {
        const nextIngredients = [...ingredients];
        nextIngredients[index].waste = value;
        setIngredients(nextIngredients);
    }

    const getUnitCost = (record) => {
        if (record.category === CategoryType.Wholesale) {
            return record.bulkCost;
        } else {
            let cost = record.cost;
            if (record.tariff) {
                cost += record.tariff;
            }
            if (record.shipping) {
                cost += record.shipping;
            }
            const unitCost = parseFloat((cost / record.startQuantity).toFixed(2));
            return unitCost;
        }
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Available',
            dataIndex: 'available',
            key: 'available',
            render: (val, record) => {
                const displayQuantity = fixedToDisplayUnit(val, record.unit);
                if (record.unit === UnitType.Each) {
                    return displayQuantity
                } else {
                    return `${displayQuantity} ${UnitType.Label[record.unit]}`
                }
            }
        },
        {
            title: 'Used',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (val, record, index) => {
                // available is fixed,  quantity and waste are display
                const maxFixed = record.available - displayToFixedUnit(record.waste, record.unit);
                const max = fixedToDisplayUnit(maxFixed, record.unit);
                return <QuantityEntryWidget title={`Quantity`} placeholder={`Quantity`} unit={record.unit} value={val} max={max} onQuantityChange={onIngredientQuantityChange(index)}/>
            }
        },
        {
            title: 'Waste',
            dataIndex: 'waste',
            key: 'waste',
            render: (val, record, index) => {
                // available is fixed,  quantity and waste are display
                const maxFixed = record.available - displayToFixedUnit(record.quantity, record.unit);
                const max = fixedToDisplayUnit(maxFixed, record.unit);
                return <QuantityEntryWidget title={`Waste`} placeholder={`Waste`} unit={record.unit} value={val} max={max} onQuantityChange={onIngredientWasteChange(index)}/>
            }
        },
        {
            title: 'Unit Cost',
            dataIndex: 'cost',
            key: 'unitcost',
            render: (val, record, index) => {
                if (record.category === CategoryType.Wholesale) {
                    return `$ ${record.bulkCost} / g`
                } else {
                    const unitCost = getUnitCost(record);
                    return `$ ${unitCost}`
                }
            }
        },
        {
            title: 'Total Cost',
            dataIndex: 'cost',
            key: 'totalcost',
            render: (val, record, index) => {
                const unitCost = getUnitCost(record);
                const cost = unitCost * (record.quantity + record.waste)
                return `$ ${cost.toFixed(2)}`
            }
        }
    ]

    if (item && item.state < PurchaseOrderItemStatusType.Complete) {
        columns.push({
            title: 'Delete',
            dataIndex: 'quantity',
            key: 'delete',
            render: (val, record, index) => {
                return <TrashWidget onDelete={onIngredientDelete(index)} />
            }
        });
    }

    const ingredientsTable = <Table dataSource={ingredients} columns={columns} pagination={false}/>


    const onProductSelect = (item) => {
        setProduct(item);
    }

    const onQuantityChange = (val) => {
        setQuantity(val);
    }

    const onHoursChange = (val) => {
        setHours(val);
    }

    const onBookQuantityChange = (val) => {
        setBookQuantity(val);
    }

    const onCompleteLineItem = () => {
        // reserve all ingredients
        const promises = [];
        ingredients.forEach(ing => {
            const fixedQuantity = displayToFixedUnit(ing.quantity, ing.unit) + displayToFixedUnit(ing.waste, ing.unit);
            promises.push(addInventoryToCart(ing.id, fixedQuantity));
        })
        Promise.all(promises).then(r => {
            // submit all the data to backend
            const cost = ingredients.reduce((acc, ingredient) => {
                const unitCost = getUnitCost(ingredient);
                const cost = unitCost * (ingredient.quantity + ingredient.waste)
                return acc + cost;
            }, 0)

            console.log(`total cost for new item ${cost}`);
            const sku = `${item.sku}-${toUtcDateString(new Date())}`

            const fixedIngredients = ingredients.map(ing => {
                return {
                    inventoryId: ing.id,
                    quantity: displayToFixedUnit(ing.quantity, ing.unit),
                    waste: displayToFixedUnit(ing.waste, ing.unit)
                }
            })
            return completePurchaseOrderLine(purchaseId, lineId, fixedIngredients, getProductId(), quantity, unit, sku, cost, bookQuantity, hours);
        }).then(r => {
            messageApi.open({
                type: 'success',
                content: "Items Successfully Created"
            });
            // clear the ingredients
            setIngredients([]);
            if (onRefresh) onRefresh();
        }).catch(e => {
            messageApi.open({
                type: 'error',
                content: "Failed to Create Purchase Order Item"
            });
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        }).finally(() => {
            // clear the cart when we are done
        });
    }



    if (item === undefined) {
        return <div />
    }

    const rowStyle={textAlign: 'left', marginTop: '5px', marginBottom: '5px'}
    const colStyle = {marginLeft: '5px', marginRight: '5px'}
    let emptyIngredient = false;
    for (let i=0; i<ingredients.length; i++) {
        if (ingredients[i].quantity === 0) {
            emptyIngredient = true;
        }
    }

    const okDisabled = (ingredients.length <= 0) || (emptyIngredient)
        || (quantity <= 0) || (getProductId() === undefined)

    const ingredientsEmpty = ingredients.length === 0;
    const hoursInvalid = (hours === null) || (hours === undefined);

    const inventoryCategory = (isBulk) ? CategoryType.Wholesale : CategoryType.Supplies;

    const itemView = <div style={{textAlign: 'left'}}>
        {contextHolder}
        <Card title={'Item to Create'}>
            <Row style={rowStyle}>
                <Col span={2}><b>Requested:</b></Col>
                <Col>{item.quantity} <b>{item.name}</b></Col>
            </Row>
            <Row style={rowStyle}>
                <Col><PurchaseOrderItemStatusWidget item={item} onApiFail={onApiFail} onRefresh={onRefresh} isFulfillment={true} /></Col>
            </Row>
        </Card>
        <Card title={
            <div>
                <Row style={{fontSize: '16px'}}>Search</Row>
                {(ingredients.length === 0) &&
                    <Row style={{color: 'red'}}>Step 1: Select the Ingredients from Inventory</Row>
                }
            </div>
        }>
            {!showInventory && <Row style={rowStyle}>
                <Col style={colStyle}>
                    <Button onClick={onOpenBulkInventory}>Select Bulk Ingredients</Button>
                </Col>
                <Col style={colStyle}>
                    <Button onClick={onOpenSupplyInventory}>Select Supplies</Button>
                </Col>
            </Row>
            }
            {showInventory && <Row style={rowStyle}>
                <Button onClick={onCloseInventory}>Close Inventory</Button>
            </Row>
            }
            {showInventory &&
            <InventorySelectWidget onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} onSelect={selectIngredient} category={inventoryCategory}/>
            }
        </Card>

        {!ingredientsEmpty && <Card title={
            <div>
                <Row style={{fontSize: '16px'}}>Ingredients</Row>
                {emptyIngredient &&
                    <Row style={{color: 'red'}}>Step 2: Enter Quantity Used & Wasted for each Ingredient</Row>
                }
            </div>
        }>

            {ingredientsTable}
        </Card>}
        { item.state < PurchaseOrderItemStatusType.Complete && !ingredientsEmpty &&!emptyIngredient &&
            <Card title={<div>
                <Row style={{fontSize: '16px'}}>Hours</Row>
                {((hours === 0) || hoursInvalid) &&
                    <Row style={{color: 'red'}}>Step 3: Enter the Number of Hours Spent</Row>}
            </div>}>
                <Row style={rowStyle}>
                    <Col><InputNumber min={0} precision={2} placeholder={"Hours"} addonAfter={`hours`} onChange={onHoursChange} /></Col>
                </Row>
            </Card>
        }
        { item.state < PurchaseOrderItemStatusType.Complete && !ingredientsEmpty && !hoursInvalid &&
        <Card title={<div>
            <Row style={{fontSize: '16px'}}>Created Inventory</Row>
            {((quantity === 0) || (quantity === null)) &&
            <Row style={{color: 'red'}}>Step 4: Enter the Product and Quantity to Add to Inventory</Row>}
        </div>}>
            <ProductDisplayWidget productId={getProductId()} onSelect={onProductSelect} refresh={refresh} onApiFail={onApiFail} />
            <Row style={rowStyle}>
                <Col span={6}><InputNumber min={0} addonBefore={<b>Total Quantity</b>} placeholder={"Quantity"} onChange={onQuantityChange} /></Col>
                <Col span={6}><InputNumber min={0} addonBefore={<b>Book Quantity</b>} max={quantity} placeholder={'Book'} onChange={onBookQuantityChange}/></Col>
                <Col style={{width: '140px', marginRight: '5px'}}><b>Non-Book Quantity:</b></Col><Col>{quantity-bookQuantity}</Col>
            </Row>
        </Card>
        }
        { item.state < PurchaseOrderItemStatusType.Complete && !ingredientsEmpty && (quantity > 0) && hasProductId() &&
        <Card title={<div>
                <Row style={{fontSize: '16px'}}>Finalize</Row>
                <Row style={{color: 'red'}}>Final Step: Finalize when everything is done.</Row>
            </div>}>
            <Row style={rowStyle}>
                <Button onClick={onCompleteLineItem} disabled={okDisabled}>Complete</Button>
            </Row>
        </Card>
        }
    </div>

    if (item.state === PurchaseOrderItemStatusType.Complete) {
        return <CompletedPOLineItemView item={item} onApiFail={onApiFail} onRefresh={onRefresh} refresh={refresh} />
    }
    return itemView;
 }